<template>
  <div class="date-stepper">
    <BaseButton
      class="date-stepper__nav mr-0"
      type="text"
      context-button="program"
      has-icon
      size="small"
      :is-disabled="disabled || isAtMin || isEmpty"
      @click="step(-1)"
    >
      <v-icon small>
        mdi-chevron-left
      </v-icon>
    </BaseButton>

    <v-menu
      v-model="menu"
      transition="scale-transition"
      offset-y
      min-width="290px"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on }">
        <button
          type="button"
          class="date-stepper__label"
          :disabled="disabled"
          v-on="on"
        >
          {{ displayText }}
        </button>
      </template>
      <div :style="{'background': isDarkTheme ? '#424242' : '#FFFFFF'}">
        <div
          v-if="range"
          class="date-stepper__presets"
        >
          <v-chip
            v-for="preset in presets"
            :key="preset"
            small
            outlined
            @click="applyPreset(preset)"
          >
            {{ preset }} dias
          </v-chip>
        </div>
        <v-date-picker
          :range="range"
          :value="pickerValue"
          no-title
          :max="max"
          :min="min"
          :disabled="disabled"
          @input="onPick"
        >
          <template v-if="!range">
            <v-spacer />
            <BaseButton
              context-button="program"
              type="text"
              @click="menu = false"
            >
              Confirmar
            </BaseButton>
          </template>
        </v-date-picker>
      </div>
    </v-menu>

    <BaseButton
      v-if="!isEmpty"
      class="date-stepper__clear mx-0"
      type="text"
      context-button="program"
      has-icon
      size="small"
      :is-disabled="disabled"
      @click="clear"
    >
      <v-icon small>
        mdi-close
      </v-icon>
    </BaseButton>

    <BaseButton
      class="date-stepper__nav mx-0"
      type="text"
      context-button="program"
      has-icon
      size="small"
      :is-disabled="disabled || isAtMax || isEmpty"
      @click="step(1)"
    >
      <v-icon small>
        mdi-chevron-right
      </v-icon>
    </BaseButton>

    <BaseButton
      type="text"
      context-button="program"
      size="small"
      class="date-stepper__today ml-0"
      :is-disabled="disabled"
      @click="goToday"
    >
      Hoje
    </BaseButton>
  </div>
</template>

<script>
export default {
  name: 'BaseDateStepper',
  props: {
    value: {
      type: [String, Object],
      required: true,
      default: ''
    },
    label: {
      type: String,
      required: false,
      default: 'Exibindo'
    },
    max: {
      type: String,
      default: null
    },
    min: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    range: {
      type: Boolean,
      required: false,
      default: false
    },
    presets: {
      type: Array,
      required: false,
      default: () => [7, 15, 30]
    }
  },
  data() {
    return {
      menu: false,
      pendingRange: null
    }
  },
  computed: {
    isDarkTheme() {
      return this.$vuetify.theme.dark
    },
    effectiveValue() {
      if (this.range) {
        if (this.value && this.isValidISODate(this.value.start) && this.isValidISODate(this.value.end)) return this.value
        return null
      }
      return this.isValidISODate(this.value) ? this.value : null
    },
    isEmpty() {
      return this.effectiveValue === null
    },
    pickerValue() {
      if (this.isEmpty) return this.pendingRange || undefined
      if (!this.range) return this.effectiveValue
      return this.pendingRange || [this.effectiveValue.start, this.effectiveValue.end]
    },
    formattedValue() {
      if (this.range) {
        return `De ${this.formatDate(this.effectiveValue.start)} — Até ${this.formatDate(this.effectiveValue.end)}`
      }
      return this.formatDate(this.effectiveValue)
    },
    displayText() {
      if (this.isEmpty) {
        return this.label || (this.range ? 'Selecionar período' : 'Selecionar data')
      }
      return this.label ? `${this.label}: ${this.formattedValue}` : this.formattedValue
    },
    isAtMax() {
      if (this.isEmpty) return false
      const end = this.range ? this.effectiveValue.end : this.effectiveValue
      return !!this.max && end >= this.max
    },
    isAtMin() {
      if (this.isEmpty) return false
      const start = this.range ? this.effectiveValue.start : this.effectiveValue
      return !!this.min && start <= this.min
    },
    isMinMaxInverted() {
      return !!this.min && !!this.max && this.min > this.max
    }
  },
  watch: {
    menu(isOpen) {
      if (!isOpen) this.pendingRange = null
    },
    isMinMaxInverted: {
      immediate: true,
      handler(inverted) {
        if (inverted && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(`BaseDateStepper: "min" (${this.min}) é maior que "max" (${this.max}) — o filtro fica sem nenhuma data navegável.`)
        }
      }
    },
    value: {
      immediate: true,
      handler(newValue) {
        this.warnIfValueInvalid(newValue)
      }
    }
  },
  methods: {
    step(direction) {
      if (this.isEmpty) return
      if (direction < 0 && this.isAtMin) return
      if (direction > 0 && this.isAtMax) return
      if (this.range) {
        const span = this.daysBetween(this.effectiveValue.start, this.effectiveValue.end) + 1
        const shift = direction * span
        this.$emit('on-input-date-picker', {
          start: this.addDays(this.effectiveValue.start, shift),
          end: this.addDays(this.effectiveValue.end, shift)
        })
        return
      }
      this.$emit('on-input-date-picker', this.addDays(this.effectiveValue, direction))
    },
    goToday() {
      const today = this.todayISO()
      this.$emit('on-input-date-picker', this.range ? { start: today, end: today } : today)
    },
    clear() {
      this.pendingRange = null
      this.menu = false
      this.$emit('on-input-date-picker', this.range ? null : '')
    },
    applyPreset(days) {
      this.pendingRange = null
      this.menu = false
      this.$emit('on-input-date-picker', this.lastNDays(days))
    },
    onPick(pickedValue) {
      if (this.range) {
        if (!Array.isArray(pickedValue)) return
        if (pickedValue.length < 2) {
          this.pendingRange = pickedValue
          return
        }
        this.pendingRange = null
        const [a, b] = pickedValue
        this.menu = false
        this.$emit('on-input-date-picker', a <= b ? { start: a, end: b } : { start: b, end: a })
        return
      }
      this.menu = false
      const valueISO = pickedValue instanceof Date ? pickedValue.toISOString().slice(0, 10) : pickedValue
      this.$emit('on-input-date-picker', valueISO)
    },
    todayISO() {
      return new Date().toISOString().slice(0, 10)
    },
    isValidISODate(value) {
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    },
    warnIfValueInvalid(value) {
      if (process.env.NODE_ENV === 'production') return
      if (this.range) {
        if (!value || typeof value !== 'object') return
        if (value.start !== undefined && !this.isValidISODate(value.start)) this.warnInvalidValue('value.start', value.start)
        if (value.end !== undefined && !this.isValidISODate(value.end)) this.warnInvalidValue('value.end', value.end)
        return
      }
      if (value && !this.isValidISODate(value)) this.warnInvalidValue('value', value)
    },
    warnInvalidValue(field, received) {
      // eslint-disable-next-line no-console
      console.warn(`BaseDateStepper: "${field}" recebido não é uma data ISO válida (YYYY-MM-DD) — "${received}". Caindo em vazio (sem filtro).`)
    },
    lastNDays(days) {
      const end = this.todayISO()
      return { start: this.addDays(end, -(days - 1)), end }
    },
    formatDate(isoDate) {
      const [year, month, day] = isoDate.split('-')
      return `${day}/${month}/${year}`
    },
    addDays(isoDate, days) {
      const [year, month, day] = isoDate.split('-').map(Number)
      const date = new Date(Date.UTC(year, month - 1, day))
      date.setUTCDate(date.getUTCDate() + days)
      return date.toISOString().slice(0, 10)
    },
    daysBetween(isoStart, isoEnd) {
      const [y1, m1, d1] = isoStart.split('-').map(Number)
      const [y2, m2, d2] = isoEnd.split('-').map(Number)
      return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000)
    }
  }
}
</script>

<style lang="scss">
.date-stepper {
  display: flex;
  align-items: center;
  gap: 2px;

  &__label {
    font-weight: 700;
    font-size: 13.5px;
    cursor: pointer;
    padding: 0 4px;
    white-space: nowrap;
    background: none;
    border: none;
    font-family: inherit;
    color: inherit;

    &:disabled {
      cursor: default;
      opacity: .5;
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  &__presets {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding: 10px 10px 0;
  }

  &__today {
    color: var(--ds-color-brand) !important;
    font-weight: 600 !important;
  }
}
</style>
